import { Box, useTheme } from "@mui/material";
import React from "react";

interface StarRatingProps {
  rating: number | null;
  size?: number;
  gap?: number | string;
  disabledFillPercentage?: number;
}

const TOTAL_STARS = 5;

const STAR_PATH =
  "M10 1.5L12.47 7.01L18.5 7.89L14.25 12.03L15.18 18.04L10 15.27L4.82 18.04L5.75 12.03L1.5 7.89L7.53 7.01L10 1.5Z";

const StarIcon: React.FC<{
  fillPercentage: number;
  filledColor: string;
  emptyColor: string;
  size: number;
}> = ({ fillPercentage, filledColor, emptyColor, size }) => {
  const clipId = React.useId();

  return (
    <svg fill="none" height={size} viewBox="0 0 20 20" width={size}>
      <defs>
        <clipPath id={clipId}>
          <rect height="20" width={20 * (fillPercentage / 100)} x="0" y="0" />
        </clipPath>
      </defs>
      {/* Star outline */}
      <path
        d={STAR_PATH}
        fill="none"
        stroke={emptyColor}
        strokeLinejoin="inherit"
        strokeWidth={1.5}
      />
      {/* Filled star overlay */}
      {fillPercentage > 0 && (
        <path
          clipPath={`url(#${clipId})`}
          d={STAR_PATH}
          fill={filledColor}
          stroke={filledColor}
          strokeLinejoin="inherit"
          strokeWidth={1.5}
        />
      )}
    </svg>
  );
};

export const StarRating: React.FC<StarRatingProps> = ({
  rating,
  size = 20,
  gap = "2px",
  disabledFillPercentage = 0,
}) => {
  const theme = useTheme();

  const isDisabled = rating === null;
  const filledColor = isDisabled
    ? theme.colors.feedback.starDisabled
    : theme.colors.feedback.starFilled;
  const emptyColor = isDisabled
    ? theme.colors.feedback.starDisabled
    : theme.colors.feedback.starEmpty;

  const stars = Array.from({ length: TOTAL_STARS }, (_, i) => {
    const position = i + 1;
    if (isDisabled) return { position, fillPercentage: disabledFillPercentage };

    let fillPercentage = 0;
    if (rating >= position) fillPercentage = 100;
    else if (rating > position - 1)
      fillPercentage = Math.round((rating - (position - 1)) * 100);

    return { position, fillPercentage };
  });

  return (
    <Box
      component="span"
      sx={{ display: "inline-flex", gap, alignItems: "center" }}
    >
      {stars.map(({ position, fillPercentage }) => (
        <StarIcon
          emptyColor={emptyColor}
          fillPercentage={fillPercentage}
          filledColor={filledColor}
          key={position}
          size={size}
        />
      ))}
    </Box>
  );
};
